/**
 * Symphony GROUP CHAT, Phase 5 — the gentle "Add your Maestro" prompt after joining a group. Accepting an invite
 * marks that conversation pending; the chat shows the banner while it is pending AND the viewer can add their
 * Maestro (`canAddMyMaestro`); "Not now" or joining clears it. Per device, cleared on sign-out (`lib/logout.ts`) —
 * the phones keep the same set in their own per-device store.
 *
 * localStorage, a new key, so no `orion-web:` prefix (orion-web CLAUDE.md).
 */
import { useSyncExternalStore } from 'react';

const KEY = 'symphony-group-maestro-prompt';
const listeners = new Set<() => void>();
let cache: string[] | null = null;

function read(): string[] {
  if (cache) return cache;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(KEY) ?? '[]') as unknown;
    cache = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    cache = [];
  }
  return cache;
}
function write(ids: string[]) {
  cache = ids;
  try {
    if (ids.length) window.localStorage.setItem(KEY, JSON.stringify(ids));
    else window.localStorage.removeItem(KEY);
  } catch {
    // private mode / quota — the prompt then lasts this page session only
  }
  listeners.forEach((l) => l());
}

export function markMaestroPromptPending(conversationId: string): void {
  if (typeof window === 'undefined' || !conversationId) return;
  const ids = read();
  if (!ids.includes(conversationId)) write([...ids, conversationId].slice(-50));
}
export function clearMaestroPrompt(conversationId: string): void {
  if (typeof window === 'undefined') return;
  const ids = read();
  if (ids.includes(conversationId)) write(ids.filter((id) => id !== conversationId));
}
/** Sign-out: the next account must not inherit this one's prompts. */
export function clearAllMaestroPrompts(): void {
  if (typeof window === 'undefined') return;
  write([]);
}

const EMPTY: string[] = [];
export function useMaestroPromptPending(conversationId: string | null | undefined): boolean {
  const ids = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => (typeof window === 'undefined' ? EMPTY : read()),
    () => EMPTY,
  );
  return !!conversationId && ids.includes(conversationId);
}
